import rules from '../config/haircutRules.json';

/**
 * Haircuts Studio generation intent + style definition helpers.
 * This file is the frontend source of truth for resolving:
 * - preset_locked
 * - suggestion_selected
 * - custom_only
 * - fallback_generated
 */

const FALLBACK_LOCK = Object.freeze({
  hairLength: 'medium',
  fadeType: 'none',
  texture: 'natural',
});

const STYLE_DEFINITION_LIBRARY = Object.freeze({
  'Textured Crop': {
    silhouette: 'short crop silhouette with visible texture and controlled top length',
    topBehavior: 'choppy textured top with clear separation and forward movement',
    sideTreatment: 'clean taper or fade on the sides with tidy blending into the crown',
    realism: 'keep the finish wearable, barbershop-clean, and visibly intentional',
  },
  'High Fade with Volume': {
    silhouette: 'high-contrast short haircut with a lifted top and tighter perimeter',
    topBehavior: 'noticeable height and volume concentrated on the top',
    sideTreatment: 'high fade with crisp transition and compact sides',
    realism: 'maintain realistic density and believable scalp coverage',
  },
  'Messy Top': {
    silhouette: 'short-to-medium modern cut with loose, casual movement on top',
    topBehavior: 'piecey messy top with soft separation and natural lived-in texture',
    sideTreatment: 'clean but not overly severe taper around sides and back',
    realism: 'keep the style editorial but still realistic for daily wear',
  },
  'Classic Side Part': {
    silhouette: 'structured classic barbershop silhouette with a clear side part',
    topBehavior: 'top directed to one side with neat comb lines and moderate volume',
    sideTreatment: 'clean tapered sides with professional blending and natural neckline',
    realism: 'keep the result polished, timeless, and realistic',
  },
  'Curly Top Fade': {
    silhouette: 'curly silhouette with fuller volume on top and tighter sides',
    topBehavior: 'defined curls kept on top with controlled height and shape',
    sideTreatment: 'fade or taper that respects the curl pattern at the transition',
    realism: 'do not straighten curls; keep believable curl clumping and density',
  },
  'Defined Curl Crop': {
    silhouette: 'short curly crop with compact shape and visible curl definition',
    topBehavior: 'tight, defined curls with a cropped controlled outline',
    sideTreatment: 'short faded or tapered sides that frame the curly top',
    realism: 'preserve natural curl spring and texture realism',
  },
  'Curly Taper': {
    silhouette: 'balanced curly haircut with soft fullness and a shaped taper',
    topBehavior: 'natural curly lift on top with clean contouring',
    sideTreatment: 'temple and nape taper that blends cleanly without flattening curls',
    realism: 'preserve true curl texture and believable shape',
  },
  'Tapered Afro Shape': {
    silhouette: 'rounded afro-inspired silhouette with clean tapering at the edges',
    topBehavior: 'structured coily volume on top with intentional shape control',
    sideTreatment: 'temple and nape taper while keeping the coily body intact',
    realism: 'preserve authentic coily density and natural texture',
  },
  'Coily High Top Fade': {
    silhouette: 'coily high-top silhouette with strong vertical shape and clean edges',
    topBehavior: 'tall coily top with sculpted shape and visible texture',
    sideTreatment: 'high fade that supports the high-top silhouette without flattening coils',
    realism: 'keep the coily texture natural, dense, and realistic',
  },
  'Protective Twist Style': {
    silhouette: 'protective twist-based silhouette with visible parting and dimensional length',
    topBehavior: 'defined twists or rope-like sections with clean separation',
    sideTreatment: 'neat perimeter and controlled sides supporting the twist layout',
    realism: 'preserve realistic twist thickness, direction, and density',
  },
  'Clean Buzz Cut': {
    silhouette: 'close-cropped buzz silhouette with uniform shape and precise edges',
    topBehavior: 'very short top with clean even reduction and subtle texture',
    sideTreatment: 'tight clean perimeter with minimal bulk and sharp outline',
    realism: 'keep scalp coverage believable and avoid artificial smoothness',
  },
  'Layered Flow Cut': {
    silhouette: 'medium-to-long layered flow silhouette with visible directional movement',
    topBehavior: 'longer top that sweeps naturally with layered separation',
    sideTreatment: 'soft scissor-over-comb blending with no severe skin fade',
    realism: 'make the layers readable and visibly longer than a short crop',
  },
  'Side Swept Medium Layers': {
    silhouette: 'medium layered silhouette with side-swept movement and soft shape',
    topBehavior: 'side-swept top with visible layering and natural bend',
    sideTreatment: 'soft blended sides with no harsh fade contrast',
    realism: 'keep the motion soft, polished, and believable',
  },
});

const STYLE_RULES = Array.isArray(rules?.recommendationEngine?.styles)
  ? rules.recommendationEngine.styles
  : [];

const STYLE_FAMILY_LABELS = Object.freeze({
  classic_barber: 'classic barbershop',
  textured_modern: 'textured modern',
  curly_afro: 'curly/coily structured',
  buzz_military: 'short buzz',
  long_layered: 'long layered flow',
  fade_taper: 'fade and taper',
  preset_locked: 'preset-locked',
});

const normalizeString = (value, fallback = '') => {
  if (typeof value !== 'string') return fallback;
  const normalized = value.trim();
  return normalized || fallback;
};

const normalizeLower = (value, fallback = 'unknown') => normalizeString(value, fallback).toLowerCase();

const inferHairLength = (preset = {}) => {
  const category = normalizeLower(preset.category, '');
  if (category === 'short' || category === 'medium' || category === 'long') return category;
  return FALLBACK_LOCK.hairLength;
};

const inferFadeType = (preset = {}) => {
  const haystack = `${preset.id || ''} ${preset.name || ''} ${preset.promptStyle || ''}`.toLowerCase();
  if (haystack.includes('skin fade')) return 'skin';
  if (haystack.includes('burst fade')) return 'burst';
  if (haystack.includes('high fade')) return 'high';
  if (haystack.includes('mid fade')) return 'mid';
  if (haystack.includes('low fade') || haystack.includes('low taper')) return 'low';
  if (haystack.includes('taper')) return 'taper';
  return FALLBACK_LOCK.fadeType;
};

const inferTexture = (preset = {}) => {
  const haystack = `${preset.id || ''} ${preset.name || ''} ${preset.promptStyle || ''}`.toLowerCase();
  if (haystack.includes('textured')) return 'textured';
  if (haystack.includes('curly') || haystack.includes('coily')) return 'curly';
  if (haystack.includes('wavy')) return 'wavy';
  if (haystack.includes('sleek') || haystack.includes('smooth')) return 'sleek';
  return FALLBACK_LOCK.texture;
};

const inferTopStyle = (preset = {}) => normalizeString(preset.name, 'Custom preset style');

const normalizeHairProfile = (hairProfile) => {
  const src = hairProfile && typeof hairProfile === 'object' ? hairProfile : {};
  return {
    curlType: normalizeLower(src.curlType),
    density: normalizeLower(src.density),
    strandThickness: normalizeLower(src.strandThickness),
    currentLength: normalizeLower(src.currentLength),
    hairline: normalizeLower(src.hairline),
    visibility: normalizeLower(src.visibility, 'limited'),
  };
};

const normalizeHaircutStructure = (haircutStructure) => {
  const src = haircutStructure && typeof haircutStructure === 'object' ? haircutStructure : {};
  return {
    family: normalizeLower(src.family, 'textured_modern'),
    topStyle: normalizeString(src.topStyle, 'Textured Top'),
    fadeHeight: normalizeLower(src.fadeHeight, 'mid'),
    fadeSkin: normalizeLower(src.fadeSkin, 'skin'),
    gradient: normalizeLower(src.gradient, 'standard'),
    shape: normalizeLower(src.shape, 'rounded'),
    blendMethod: normalizeLower(src.blendMethod, 'clipper'),
    disconnection: normalizeLower(src.disconnection, 'none'),
    lineup: normalizeLower(src.lineup, 'natural'),
    neckline: normalizeLower(src.neckline, 'tapered'),
  };
};

const getStyleRuleByName = (styleName) => {
  const target = normalizeString(styleName).toLowerCase();
  return STYLE_RULES.find((style) => normalizeString(style?.name).toLowerCase() === target) || null;
};

const buildColorInstruction = (colorMode, colorValue) => {
  if (!colorMode || colorMode === 'no_change' || !colorValue) return '';
  return `Target hair color: ${colorValue} while keeping the result realistic and natural.`;
};

const familyLabel = (family) => STYLE_FAMILY_LABELS[family] || family.replace(/_/g, ' ');

const buildStructureSummary = (structure) => (
  `Haircut structure: ${familyLabel(structure.family)} family; top style ${structure.topStyle}; ` +
  `${structure.fadeHeight} fade; skin level ${structure.fadeSkin}; ${structure.gradient} gradient; ` +
  `${structure.shape} outline; ${structure.blendMethod} blending; ${structure.disconnection} disconnection; ` +
  `${structure.lineup} lineup; ${structure.neckline} neckline.`
);

const buildHairProfileSummary = (hairProfile) => (
  `Visible hair profile: ${hairProfile.curlType} texture, ${hairProfile.density} density, ` +
  `${hairProfile.strandThickness} strand thickness, ${hairProfile.currentLength} current length, ` +
  `${hairProfile.hairline} hairline, visibility ${hairProfile.visibility}.`
);

const buildStyleDefinitionFromRule = ({ styleName, haircutStructure, hairProfile }) => {
  const rule = getStyleRuleByName(styleName);
  const libraryEntry = STYLE_DEFINITION_LIBRARY[styleName] || {};
  const structure = normalizeHaircutStructure(haircutStructure);
  const profile = normalizeHairProfile(hairProfile);
  const compatibleLengths = Array.isArray(rule?.compatibility?.currentLength)
    ? rule.compatibility.currentLength.join('/')
    : structure.fadeHeight;

  const segments = [
    `${styleName}: ${libraryEntry.silhouette || `${familyLabel(structure.family)} silhouette with a clear ${styleName.toLowerCase()} direction`}.`,
    `Top behavior: ${libraryEntry.topBehavior || `${structure.topStyle} with visible definition and intentional shape`}.`,
    `Side treatment: ${libraryEntry.sideTreatment || `${structure.fadeHeight} fade with ${structure.blendMethod} blending and ${structure.lineup} lineup`}.`,
    `Length goal: ${compatibleLengths}.`,
    libraryEntry.realism || `Adapt the haircut realistically to ${profile.curlType} hair with ${profile.density} density.`,
  ];

  return segments.filter(Boolean).join(' ');
};

const buildPresetDefinition = ({ presetLock, haircutStructure, hairProfile }) => {
  const lock = presetLock || {};
  const structure = normalizeHaircutStructure({
    ...haircutStructure,
    ...(lock.haircutStructure || {}),
  });
  const profile = normalizeHairProfile(hairProfile);

  return [
    `${lock.styleName || 'Selected preset'}: ${lock.styleDirective || 'follow the chosen preset silhouette exactly'}.`,
    `Preset target: ${lock.hairLength || 'medium'} length, ${lock.fadeType || 'none'} fade behavior, ${lock.texture || 'natural'} texture direction.`,
    buildStructureSummary(structure),
    `Adapt only micro-details for ${profile.curlType} hair and ${profile.density} density without changing the preset family.`,
  ].filter(Boolean).join(' ');
};

const buildFallbackStyleDefinition = ({ haircutStructure, hairProfile }) => {
  const structure = normalizeHaircutStructure(haircutStructure);
  const profile = normalizeHairProfile(hairProfile);
  return [
    `Controlled fallback haircut: ${familyLabel(structure.family)} style with ${structure.topStyle}.`,
    `Create a visible transformation using ${structure.fadeHeight} fade behavior, ${structure.gradient} gradient, ${structure.blendMethod} blending, and ${structure.shape} silhouette.`,
    `Adapt the result to ${profile.curlType} hair, ${profile.density} density, and ${profile.currentLength} current length while keeping the cut realistic.`,
  ].join(' ');
};

const buildCustomStyleDefinition = ({ userCustomPrompt, haircutStructure, hairProfile }) => {
  const structure = normalizeHaircutStructure(haircutStructure);
  const profile = normalizeHairProfile(hairProfile);
  return [
    `Custom hairstyle request: ${normalizeString(userCustomPrompt)}.`,
    buildStructureSummary(structure),
    `Adapt the custom request to ${profile.curlType} hair, ${profile.density} density, and ${profile.currentLength} current length without breaking realism.`,
  ].join(' ');
};

export const buildPresetGenerationLock = (preset) => {
  if (!preset || typeof preset !== 'object') return null;

  const base = preset.generationLock && typeof preset.generationLock === 'object'
    ? preset.generationLock
    : {};

  return {
    presetId: normalizeString(preset.id || base.presetId),
    styleName: normalizeString(preset.name || base.styleName),
    category: normalizeLower(preset.category || base.category || 'medium', 'medium'),
    hairLength: normalizeLower(base.hairLength || inferHairLength(preset), FALLBACK_LOCK.hairLength),
    fadeType: normalizeLower(base.fadeType || inferFadeType(preset), FALLBACK_LOCK.fadeType),
    texture: normalizeLower(base.texture || inferTexture(preset), FALLBACK_LOCK.texture),
    styleDirective: normalizeString(base.styleDirective || preset.promptStyle),
    haircutStructure: {
      topStyle: normalizeString(base.haircutStructure?.topStyle || inferTopStyle(preset), inferTopStyle(preset)),
      fadeHeight: normalizeLower(base.haircutStructure?.fadeHeight || inferFadeType(preset), inferFadeType(preset)),
      family: normalizeLower(base.haircutStructure?.family || 'preset_locked', 'preset_locked'),
      disconnection: normalizeLower(base.haircutStructure?.disconnection || 'none', 'none'),
      fadeSkin: normalizeLower(base.haircutStructure?.fadeSkin || 'skin', 'skin'),
      gradient: normalizeLower(base.haircutStructure?.gradient || 'standard', 'standard'),
      shape: normalizeLower(base.haircutStructure?.shape || 'rounded', 'rounded'),
      blendMethod: normalizeLower(base.haircutStructure?.blendMethod || 'clipper', 'clipper'),
      lineup: normalizeLower(base.haircutStructure?.lineup || 'natural', 'natural'),
      neckline: normalizeLower(base.haircutStructure?.neckline || 'tapered', 'tapered'),
    },
    strictConstraints: Array.isArray(base.strictConstraints) && base.strictConstraints.length > 0
      ? base.strictConstraints
      : [
          'Do not switch to another hairstyle family.',
          'Keep overall haircut silhouette faithful to this preset.',
          'Apply only minimal adaptation required for visible hair texture and density.',
        ],
  };
};

export const buildResolvedStyleDefinition = ({
  generationMode,
  resolvedStyleName,
  selectedSuggestionDefinition,
  presetLock,
  haircutStructure,
  hairProfile,
  userCustomPrompt,
  colorMode,
  colorValue,
}) => {
  let definition = '';
  if (generationMode === 'suggestion_selected') {
    definition = normalizeString(selectedSuggestionDefinition)
      || buildStyleDefinitionFromRule({ styleName: resolvedStyleName, haircutStructure, hairProfile });
  } else if (generationMode === 'preset_locked') {
    definition = buildPresetDefinition({ presetLock, haircutStructure, hairProfile });
  } else if (generationMode === 'custom_only') {
    definition = buildCustomStyleDefinition({ userCustomPrompt, haircutStructure, hairProfile });
  } else {
    definition = buildFallbackStyleDefinition({ haircutStructure, hairProfile });
  }

  const colorInstruction = buildColorInstruction(colorMode, colorValue);
  return [definition, colorInstruction].filter(Boolean).join(' ');
};

export const resolveGenerationIntent = ({
  gender,
  preset,
  selectedStyle,
  userCustomPrompt,
  colorMode,
  colorValue,
  faceShape,
  hairProfile,
  haircutStructure,
}) => {
  const selectedPresetLock = buildPresetGenerationLock(preset);
  const normalizedStructure = normalizeHaircutStructure(
    selectedPresetLock?.haircutStructure || haircutStructure
  );
  const normalizedHairProfile = normalizeHairProfile(hairProfile);
  const customPrompt = normalizeString(userCustomPrompt);
  const suggestionName = normalizeString(selectedStyle);

  let generationMode = 'fallback_generated';
  let resolvedStyleName = normalizedStructure.topStyle || 'Structured Haircut';

  if (suggestionName) {
    generationMode = 'suggestion_selected';
    resolvedStyleName = suggestionName;
  } else if (selectedPresetLock?.styleName) {
    generationMode = 'preset_locked';
    resolvedStyleName = selectedPresetLock.styleName;
  } else if (customPrompt) {
    generationMode = 'custom_only';
    resolvedStyleName = 'Custom hairstyle request';
  }

  const selectedSuggestionDefinition = generationMode === 'suggestion_selected'
    ? buildStyleDefinitionFromRule({
        styleName: suggestionName,
        haircutStructure: normalizedStructure,
        hairProfile: normalizedHairProfile,
      })
    : '';

  const resolvedStyleDefinition = buildResolvedStyleDefinition({
    generationMode,
    resolvedStyleName,
    selectedSuggestionDefinition,
    presetLock: selectedPresetLock,
    haircutStructure: normalizedStructure,
    hairProfile: normalizedHairProfile,
    userCustomPrompt: customPrompt,
    colorMode,
    colorValue,
  });

  return {
    generationMode,
    gender: normalizeLower(gender, 'other'),
    faceShape: normalizeLower(faceShape, 'unknown'),
    haircutStructure: normalizedStructure,
    hairProfile: normalizedHairProfile,
    presetLock: generationMode === 'preset_locked' ? selectedPresetLock : null,
    selectedSuggestionName: generationMode === 'suggestion_selected' ? suggestionName : '',
    selectedSuggestionDefinition,
    resolvedStyleName,
    resolvedStyleDefinition,
    userCustomPrompt: customPrompt,
    previewPrompt: [
      `Generation mode: ${generationMode}`,
      `Resolved style: ${resolvedStyleName}`,
      `Resolved definition: ${resolvedStyleDefinition}`,
      buildStructureSummary(normalizedStructure),
      buildHairProfileSummary(normalizedHairProfile),
    ].join('\n'),
  };
};

export const buildGenerationPayload = ({
  file,
  intent,
  scanId,
}) => ({
  file,
  custom_hint: intent.userCustomPrompt || undefined,
  style: intent.generationMode === 'suggestion_selected' ? intent.selectedSuggestionName : undefined,
  faceShape: intent.faceShape || undefined,
  hairProfile: intent.hairProfile,
  haircutStructure: intent.haircutStructure,
  scan_id: scanId || undefined,
  generationMode: intent.generationMode,
  selectedSuggestionName: intent.selectedSuggestionName || undefined,
  selectedSuggestionDefinition: intent.selectedSuggestionDefinition || undefined,
  resolvedStyleName: intent.resolvedStyleName || undefined,
  resolvedStyleDefinition: intent.resolvedStyleDefinition || undefined,
  presetId: intent.presetLock?.presetId || undefined,
  presetName: intent.presetLock?.styleName || undefined,
  presetCategory: intent.presetLock?.category || undefined,
  presetLock: intent.presetLock || undefined,
});

export function buildUserFriendlyPrompt({
  styleName,
  styleDefinition,
  hairProfile,
}) {
  const name = normalizeString(styleName);
  const definition = normalizeString(styleDefinition);
  if (!name && !definition) return '';

  const sentences = definition
    .split('.')
    .map((part) => part.trim())
    .filter(Boolean)
    .slice(0, 2);

  const shortDescription = sentences.length > 0
    ? `${sentences.join('. ')}.`
    : '';

  const curlType = normalizeString(hairProfile?.curlType, 'natural');
  const density = normalizeString(hairProfile?.density, 'normal');

  return [
    name || 'Hairstyle Applied',
    shortDescription,
    `• Adapted to your hair type (${curlType}, ${density})`,
    '• Realistic barbershop result',
  ]
    .filter(Boolean)
    .join('\n\n');
}

/**
 * Backwards-compatible preview builder used in some UI paths.
 */
export const buildHaircutPrompt = ({ gender, preset, colorMode, colorValue, userCustomPrompt }) => {
  const intent = resolveGenerationIntent({
    gender,
    preset,
    selectedStyle: '',
    userCustomPrompt,
    colorMode,
    colorValue,
    faceShape: 'unknown',
    hairProfile: null,
    haircutStructure: null,
  });
  return intent.previewPrompt;
};

/**
 * Get the gender prefix for display purposes
 * @param {string} gender - 'men', 'women', or 'other'
 * @returns {string} - Gender prefix
 */
export const getGenderPrefix = (gender) => {
  switch (gender.toLowerCase()) {
    case 'men':
      return 'A man with';
    case 'women':
      return 'A woman with';
    case 'other':
      return 'A person with';
    default:
      return 'A person with';
  }
};

/**
 * Validate prompt parameters
 * @param {Object} params - Parameters to validate
 * @returns {Object} - { isValid: boolean, errors: string[] }
 */
export const validatePromptParams = (params) => {
  const errors = [];

  if (!params.gender) {
    errors.push('Gender is required');
  }

  if (!params.preset) {
    errors.push('Haircut preset is required');
  }

  if (params.colorMode === 'custom' && !params.colorValue) {
    errors.push('Custom color value is required when custom mode is selected');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Build a preview of the prompt without executing
 * @param {Object} params - Same as buildHaircutPrompt
 * @returns {Object} - { prefix: string, style: string, color: string, constraints: string }
 */
export const buildPromptPreview = ({ gender, preset, colorMode, colorValue }) => {
  const intent = resolveGenerationIntent({
    gender,
    preset,
    selectedStyle: '',
    userCustomPrompt: '',
    colorMode,
    colorValue,
    faceShape: 'unknown',
    hairProfile: null,
    haircutStructure: null,
  });

  return {
    prefix: getGenderPrefix(gender),
    style: intent.resolvedStyleName,
    color: colorMode !== 'no_change' && colorValue ? `Change hair color to ${colorValue}` : null,
    constraints: intent.resolvedStyleDefinition,
  };
};

export default buildHaircutPrompt;

